import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import findIndex from 'lodash/findIndex';
import map from 'lodash/map';
import has from 'lodash/has';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import invert from 'lodash/invert';
import mapValues from 'lodash/mapValues';
import escapeRegExp from 'lodash/escapeRegExp';
import replaceall from 'replaceall';

const TOKEN_CHARS = {
    BRACKET_OPEN: '{',
    BRACKET_CLOSE: '}',
};

const CHARS_TOKENS = invert(TOKEN_CHARS);

const TOKEN_TYPES = {
    ...TOKEN_CHARS,
    STRING: 'string'
};

export function lex(str) {
    let tokens = [],
        tokenStr = '';

    for(let i = 0; i < str.length; i++) {
        let c = str[i];

        if(c in CHARS_TOKENS) {

            if(tokenStr.length > 0) {
                tokens.push({type: TOKEN_TYPES.STRING, value: tokenStr});
            }

            tokenStr = '';
            tokens.push({ type: c, value: c });
        } else {
            tokenStr+=c;
        }
    }

    if(tokenStr.length > 0) {
        tokens.push({type: TOKEN_TYPES.STRING, value: tokenStr});
    }

    return tokens;
}

export function sectionize(tokens, isStart, isEnd) {
    let before = [],
        after = [],
        startIndex = undefined,
        endIndex = undefined;

    for(let i = 0; i < tokens.length; i++) {
        let token = tokens[i];

        if(isStart(token)) {
            startIndex = i;
            break;
        }

        before.push(token);
    }

    for(let i = tokens.length-1; i > 0; i--) {
        let token = tokens[i];

        if(isEnd(token)) {
            endIndex = i;
            break;
        }

        after.unshift(token);
    }

    if([typeof startIndex, typeof endIndex].filter(t => t === 'undefined').length === 1) {
        throw new Error('Failed to find matching pair ');
    }

    if(typeof startIndex === 'undefined' && typeof endIndex === 'undefined') {
        return {
            before: tokens,
            center: [],
            after: []
        }
    }

    return {
        before,
        center: tokens.slice(startIndex+1, endIndex),
        after,
    }
}

export function buildTokenTree(tokens, isStart, isEnd, process = t => t) {
    let sections = sectionize(tokens, isStart, isEnd),
        tree = [...sections.before];

    // console.log('===========');
    // console.log('TOKENS', tokens);
    // console.log('SECTIONS', sections);
    // console.log('===========');

    if(sections.center.length) {
        tree.push(buildTokenTree(sections.center, isStart, isEnd, process));
    }

    tree = process([...tree, ...sections.after]);

    return tree;
}

export function compileRule(rule, schema = {}) {
    let ruleName = rule.name,
        parameterRx = /\{(.*?)\}/g,
        ruleKeys = [],
        ruleExpression = null,
        ruleRegex = null,
        match;

    const replacePlaceholders = (str) => {
        let regex = escapeRegExp(str)
            .replace(/\\\{(.*?)\\\}/g, '([^.]*?)');

        return `^${regex}$`;
    };

    while ((match = parameterRx.exec(ruleName)) != null) {
        ruleKeys.push(match[1]);
    }

    if(ruleKeys.length) {
        ruleExpression = replacePlaceholders(ruleName);
        ruleRegex = new RegExp(ruleExpression);
    }
    
    let ret = {
        ...rule,
        path: rule.path ? rule.path : (rule.path === false ? false : rule.name),
        value: rule.value ? rule.value: schema.value,
        ignore: rule.ignore ? true : !!rule.path,
        ruleKeys,
        ruleExpression,
        ruleRegex
    };

    return ret;
}

export function matchRule(rule, candidate) {
    let { ruleRegex, ruleKeys } = rule;
    let objectPath = rule.path;
    let parameters = {};

    if(!ruleRegex) {
        if(rule.name === candidate) {
            return { parameters, objectPath };
        }
    }

    let match = candidate.match(ruleRegex);

    if(!match) { return false; }

    let values = match.slice(1);

    for(let i = 0; i < values.length; i++) {
        let key = ruleKeys[i],
            value = values[i];

        if(objectPath) {
            objectPath = replaceall(`{${key}}`, value, objectPath);
        }

        parameters[ruleKeys[i]] = values[i];
    }

    return {
        objectPath,
        parameters
    };
}

const defaultSchema = {
    value: (s, {objectPath}) => get(s, objectPath),
};

export function compileSchema(schema) {
    schema = {
        ...defaultSchema,
        ...schema,
    };

    return {
        ...schema,
        rules: map(schema.rules, (rule) => compileRule(rule, schema))
    };
}

export function compilePath(path) {
    let tokens = lex(path);
    let tree = buildTokenTree(
        tokens,
        t => t.type === TOKEN_TYPES.BRACKET_OPEN,
        t => t.type === TOKEN_TYPES.BRACKET_CLOSE,
        (tokens) => {
            let compiledTokens = [];
            for(let i = 0; i < tokens.length; i++) {
                let token = tokens[i];

                if(token.type !== TOKEN_TYPES.STRING) {
                    continue;
                }

                let tokenNames = token.value.split(',');
                for(let j = 0; j < tokenNames.length; j++) {
                    let name = tokenNames[j],
                        options = [];

                    if(j === (tokenNames.length-1)) {
                        options = get(tokens, `${i+1}`, []);
                    }

                    compiledTokens.push({name, options})
                }
            }
            
            return compiledTokens;
        }
    );

    return tree[0];
}

export function normalizeDefinitionGroups(definitionGroups) {
    return definitionGroups;
};

export function compilePaths(definitionGroups) {
    // console.log('compilePaths', definitionGroups);

    return mapValues(definitionGroups, (definitionGroup) => {
        definitionGroup = {
            ...definitionGroup,
            groups: mapValues(definitionGroup.groups, (subGroup) => {
                return {
                    ...subGroup,
                    paths: subGroup.paths.map(compilePath)
                }
            })
        };

        return definitionGroup;
    });
}

const mergePathOptions = (target, ...sources) => {
    for(let source of sources) {
        for(let option of source) {
            if(findIndex(target, {name: option.name}) >-1) {
                continue;
            }

            target.push(option);
        }
    }

    return target;
};

export function concludeObject(object, definitionGroups, schema) {
    const matchRuleCandidate = (candidate) => {
        for(let rule of schema.rules) {
            let matchedRule = matchRule(rule, candidate);

            if(!matchedRule) { continue; }

            return { ...rule, ...matchedRule };
        }

        return null;
    };

    const concludeAboutPath = (pathData, group, prev) => {
        let startPath = pathData.name,
            rule = matchRuleCandidate(pathData.name),
            reduce = group.reduce;

        // console.log('CONCLUDING [PENDING]'+startPath);

        const emitDefault = () => {
            let emitted = [];
            for(let option of pathData.options) {
                let defaultPath = {
                    ...option,
                    name: `${startPath}.${option.name}`
                };

                emitted.push(defaultPath);
            }

            return emitted;
        };

        const emitCustom = (props) => {
            let emitted = rule.emit(object, props);

            emitted = emitted.reduce((alreadyEmitted, emittedPath) => {
                let compiledPath = compilePath(emittedPath);

                if(!pathData.options.length) {
                    alreadyEmitted.push(compiledPath);
                    return alreadyEmitted;
                }

                for(let option of pathData.options) {
                    let optionizedPath = {
                        ...compiledPath,
                        name: `${compiledPath.name}.${option.name}`,
                        options: mergePathOptions([], option.options, compiledPath.options)
                    };

                    alreadyEmitted.push(optionizedPath);
                }

                return alreadyEmitted;
            }, []);

            return emitted;
        };
        
        if(!rule) {
            if(pathData.options.length) {
                for(let option of pathData.options) {
                    let optionizedPath = `${startPath}.${option.name}`;
                    if(!concludeAboutPath({ name: optionizedPath, options: option.options }, group, prev)) {
                        return false;
                    }
                }

                return true;
            } else {
                throw new Error('Rule with name '+pathData.name+' not found!')
            }
        }

        let emit = rule.emit ? emitCustom : emitDefault;

        let params = {
            startPath,
            object,
            objectPath: rule.objectPath,
            parameters: rule.parameters,
        };

        params.value = rule.value(object, params);

        if(!rule.ignore) {
            let prevTmp = reduce(prev.value, params);

            // console.log('CONCLUDING [OK] '+startPath, params.value, prevTmp);

            if(has(prevTmp, '_break')) {
                prev.value = prevTmp._break;
                return false;
            } else {
                prev.value = prevTmp;
            }
        }

        let emitted = emit(params);
        for(let pathData of emitted) {
            if(!concludeAboutPath(pathData, group, prev)) {
                return false;
            }
        }

        return true;
    };

    return mapValues(definitionGroups, (definitionGroup) => {
        let { groups } = definitionGroup,
            definitionGroupCollector = { value: cloneDeep(definitionGroup.value) };

        for(let groupName in groups) {
            if(!groups.hasOwnProperty(groupName)) { continue; }

            let group = groups[groupName],
                groupCollector = { value: cloneDeep(group.value) };

            for (let path of group.paths) {
                if(!concludeAboutPath(path, group, groupCollector)) {
                    break;
                }
            }

            let tmp = definitionGroup.reduce(definitionGroupCollector.value, {name: groupName, value: groupCollector.value});
            if(has(tmp, '_break')) {
                definitionGroupCollector.value = tmp._break;
                break;
            } else {
                definitionGroupCollector.value = tmp;
            }
        }

        return definitionGroupCollector.value;
    });
}

export default function concludeAbout(schema) {
    if(isArray(schema)) {
        schema = {
            rules: schema
        };
    }
    
    schema = compileSchema(schema);

    return (pathsVariadic, maybeReduce = null, maybeInitialValue = null) => {
        let paths = null;

        if(isString(pathsVariadic)) {
            pathsVariadic = [pathsVariadic]; 
        }

        if(isArray(pathsVariadic)) {
            paths = compilePaths({
                defaultDefinitions: {
                    value: null,
                    reduce: (prev, {value}) => value,

                    groups: {
                        defaultGroup: {
                            reduce: maybeReduce,
                            value: maybeInitialValue,
                            paths: pathsVariadic
                        }
                    }
                }
            });

            return (object) => {
                let concluded = concludeObject(object, paths, schema);
                return concluded.defaultDefinitions;
            };
        }

        if(isObject(pathsVariadic)) {
            paths = normalizeDefinitionGroups(pathsVariadic);
            paths = compilePaths(paths);

            return (object) => {
                return concludeObject(object, paths, schema);
            };
        }
        
        throw new Error('Invalid parameters');
    }
}